import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useAudio } from "@/context/GameFeel";

const FACES = {
  idle: { eyes: "open", mouth: "M30 56 Q40 62 50 56" },
  happy: { eyes: "open", mouth: "M28 54 Q40 66 52 54" },
  cheer: { eyes: "star", mouth: "M28 54 Q40 70 52 54 Z" },
  sad: { eyes: "droop", mouth: "M30 62 Q40 54 50 62" },
  think: { eyes: "squint", mouth: "M32 60 L48 60" },
};

function Eyes({ kind }) {
  if (kind === "star") return (<>
    <path d="M27 38 l2.2 4.6 5 .7-3.6 3.5.9 5-4.5-2.4-4.5 2.4.9-5-3.6-3.5 5-.7z" fill="#fde68a" />
    <path d="M53 38 l2.2 4.6 5 .7-3.6 3.5.9 5-4.5-2.4-4.5 2.4.9-5-3.6-3.5 5-.7z" fill="#fde68a" />
  </>);
  if (kind === "droop") return (<>
    <path d="M22 42 Q28 38 34 44" stroke="#a5f3fc" strokeWidth="3" fill="none" strokeLinecap="round" />
    <path d="M46 44 Q52 38 58 42" stroke="#a5f3fc" strokeWidth="3" fill="none" strokeLinecap="round" />
    <circle cx="28" cy="48" r="3" fill="#a5f3fc" /><circle cx="52" cy="48" r="3" fill="#a5f3fc" />
  </>);
  if (kind === "squint") return (<>
    <path d="M22 44 L34 44" stroke="#a5f3fc" strokeWidth="4" fill="none" strokeLinecap="round" />
    <circle cx="52" cy="44" r="5" fill="#a5f3fc" />
  </>);
  return (<>
    <motion.g animate={{ scaleY: [1, 1, 0.1, 1, 1] }} transition={{ repeat: Infinity, duration: 4, times: [0, 0.9, 0.93, 0.96, 1] }} style={{ originY: "44px", originX: "40px" }}>
      <circle cx="28" cy="44" r="5.5" fill="#a5f3fc" /><circle cx="52" cy="44" r="5.5" fill="#a5f3fc" />
      <circle cx="30" cy="42" r="2" fill="#fff" /><circle cx="54" cy="42" r="2" fill="#fff" />
    </motion.g>
  </>);
}

export function Volt({ mood = "idle", size = 72, className = "" }) {
  const reduce = useReducedMotion();
  const f = FACES[mood] || FACES.idle;
  const anim = reduce ? {} : mood === "cheer" ? { y: [0, -10, 0], rotate: [0, -6, 6, 0] } : mood === "sad" ? { rotate: [0, -3, 0] } : { y: [0, -3, 0] };
  const tr = mood === "cheer" ? { repeat: Infinity, duration: 0.6 } : { repeat: Infinity, duration: 2.6, ease: "easeInOut" };
  return (
    <motion.svg data-testid="volt-mascot" data-mood={mood} viewBox="0 0 80 100" width={size} height={size * 1.25} className={className} animate={anim} transition={tr} aria-label="Volt, the PhonePlay host">
      <defs>
        <linearGradient id="voltBody" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#1e1b4b" /><stop offset="1" stopColor="#0f0f1f" /></linearGradient>
        <linearGradient id="voltScreen" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#312e81" /><stop offset="1" stopColor="#1e1b4b" /></linearGradient>
      </defs>
      <line x1="40" y1="14" x2="40" y2="6" stroke="#a78bfa" strokeWidth="3" strokeLinecap="round" />
      <path d="M40 0 l-4 7 h3.5 l-2 6 6-8 h-3.5z" fill="#fbbf24" />
      <rect x="12" y="14" width="56" height="76" rx="12" fill="url(#voltBody)" stroke="#8b5cf6" strokeWidth="2.5" />
      <rect x="18" y="24" width="44" height="50" rx="8" fill="url(#voltScreen)" />
      <Eyes kind={f.eyes} />
      <path d={f.mouth} stroke="#fbbf24" strokeWidth="3" fill={mood === "cheer" ? "#f472b6" : "none"} strokeLinecap="round" />
      <circle cx="40" cy="82" r="3.5" fill="#f59e0b" />
      <motion.rect x="2" y="44" width="8" height="22" rx="4" fill="#8b5cf6" animate={reduce ? {} : mood === "cheer" ? { rotate: [0, -40, 0] } : {}} transition={{ repeat: Infinity, duration: 0.6 }} style={{ originX: "6px", originY: "46px" }} />
      <motion.rect x="70" y="44" width="8" height="22" rx="4" fill="#8b5cf6" animate={reduce ? {} : mood === "cheer" ? { rotate: [0, 40, 0] } : {}} transition={{ repeat: Infinity, duration: 0.6 }} style={{ originX: "74px", originY: "46px" }} />
      <rect x="22" y="90" width="12" height="7" rx="3" fill="#6d28d9" /><rect x="46" y="90" width="12" height="7" rx="3" fill="#6d28d9" />
    </motion.svg>
  );
}

// Inline card: mascot + speech bubble (home greeting, duel intro, results, tournament shout-out).
export function VoltSay({ text, mood = "happy", label = "Volt · your host", size = 64, testid = "volt-say", accent = "amber" }) {
  const tone = accent === "emerald" ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5";
  return (
    <div data-testid={testid} className={`flex items-center gap-3 rounded-2xl border ${tone} px-3 py-2.5 text-left`}>
      <Volt mood={mood} size={size} />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{label}</p>
        <p data-testid={`${testid}-text`} className="text-sm sm:text-base font-semibold text-white leading-snug">{text}</p>
      </div>
    </div>
  );
}

// Floating Volt (mounted once in Layout): reacts to wins/losses from any game.
export function VoltBubble() {
  const { bubble, dismiss } = useAudio();
  return (
    <AnimatePresence>
      {bubble && (
        <motion.button type="button" key={bubble.id} data-testid="volt-bubble" onClick={dismiss}
          initial={{ opacity: 0, y: 24, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.95 }}
          className="fixed bottom-20 right-3 sm:bottom-24 sm:right-6 z-[70] flex items-end gap-2 max-w-[320px] text-left">
          <div className="glass rounded-2xl rounded-br-sm border border-purple-500/40 px-4 py-3 shadow-[0_0_24px_rgba(139,92,246,0.35)] mb-6">
            <p className="text-[10px] uppercase tracking-widest text-purple-300 font-semibold">Volt</p>
            <p data-testid="volt-bubble-text" className="text-sm font-semibold text-white">{bubble.text}</p>
          </div>
          <Volt mood={bubble.mood} size={56} />
        </motion.button>
      )}
    </AnimatePresence>
  );
}
